import { Link, useLocation } from 'react-router-dom'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ChevronRightIcon } from 'lucide-react'

interface BreadcrumbConfig {
  [key: string]: {
    label: string
    href?: string
  }
}

const breadcrumbConfig: BreadcrumbConfig = {
  '/': { label: 'Home', href: '/' },
  '/video': { label: 'Video' },
  '/image': { label: 'Image' },
  '/image-analysis/:runId': { label: 'Image' },
  '/video-analysis/:runId': { label: 'Video' },
  '/compare-image-analyses': { label: 'Compare Analyses' },
  '/pricing': { label: 'Plans' },
  '/history': { label: 'History' },
}

// Helper function to match dynamic routes
const matchRoute = (pathname: string, pattern: string): boolean => {
  // Handle exact matches first
  if (pathname === pattern) return true

  // Handle dynamic segments (e.g., :runId)
  const patternParts = pattern.split('/')
  const pathnameParts = pathname.split('/')

  if (patternParts.length !== pathnameParts.length) return false

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i]
    const pathnamePart = pathnameParts[i]

    // Skip dynamic segments (start with :)
    if (patternPart.startsWith(':')) continue

    // Exact match required for non-dynamic segments
    if (patternPart !== pathnamePart) return false
  }

  return true
}

export const useBreadcrumbs = () => {
  const location = useLocation()
  const pathname = location.pathname

  const generateBreadcrumbs = () => {
    if (pathname === '/') {
      return null
    }

    // Find matching config by checking patterns
    let config = null
    for (const [pattern, cfg] of Object.entries(breadcrumbConfig)) {
      if (matchRoute(pathname, pattern)) {
        config = cfg
        break
      }
    }

    if (!config) return null

    return (
      <Breadcrumb>
        <BreadcrumbList className="text-md">
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>/ </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{config.label}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }

  return {
    breadcrumbs: generateBreadcrumbs(),
  }
}
